export const mfaKeys = {
  all: ["mfa"] as const,
  status: () => [...mfaKeys.all, "status"] as const,
};

export const orgKeys = {
  all: ["orgs"] as const,
  me: () => [...orgKeys.all, "me"] as const,
  myInvites: () => ["my-invites"] as const,
};

export const orgMemberKeys = {
  all: ["org-members"] as const,
  list: (orgId: string) => [...orgMemberKeys.all, "list", orgId] as const,
};

export const facilityKeys = {
  all: ["facilities"] as const,
  list: (orgId: string) => [...facilityKeys.all, "list", orgId] as const,
  detail: (orgId: string, facilityId: string) =>
    [...facilityKeys.all, "detail", orgId, facilityId] as const,
  contacts: (orgId: string, facilityId: string) =>
    [...facilityKeys.all, "contacts", orgId, facilityId] as const,
  typeChangePreview: (orgId: string, facilityId: string, facilityType: string) =>
    [...facilityKeys.all, "type-change-preview", orgId, facilityId, facilityType] as const,
};

export interface CrossFacilityFilters {
  facility_id?: string;
  location_type?: string;
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_dir?: string;
  lat_min?: number;
  lat_max?: number;
  lng_min?: number;
  lng_max?: number;
}

export interface MapLocationFilters {
  facility_id?: string;
  location_type?: string;
  status?: string;
  search?: string;
}

export const monitoringLocationKeys = {
  all: ["monitoring-locations"] as const,
  list: (orgId: string, facilityId: string, filters?: Record<string, unknown>) =>
    [
      ...monitoringLocationKeys.all,
      "list",
      orgId,
      facilityId,
      ...(filters ? [filters] : []),
    ] as const,
  crossFacilityList: (orgId: string, filters?: CrossFacilityFilters) =>
    [
      ...monitoringLocationKeys.all,
      "cross-facility",
      "list",
      orgId,
      ...(filters ? [filters] : []),
    ] as const,
  mapList: (orgId: string, filters?: MapLocationFilters) =>
    [...monitoringLocationKeys.all, "map", orgId, ...(filters ? [filters] : [])] as const,
  detail: (orgId: string, facilityId: string, locationId: string) =>
    [...monitoringLocationKeys.all, "detail", orgId, facilityId, locationId] as const,
};

export const attachmentKeys = {
  all: ["attachments"] as const,
};

export const parameterKeys = {
  all: ["parameters"] as const,
  lists: () => [...parameterKeys.all, "list"] as const,
  list: (filters?: Record<string, unknown>) =>
    [...parameterKeys.all, "list", ...(filters ? [filters] : [])] as const,
  detail: (parameterId: string) => [...parameterKeys.all, "detail", parameterId] as const,
  orgDetail: (orgId: string, parameterId: string) =>
    [...parameterKeys.all, "detail", orgId, parameterId] as const,
  byCode: (code: string) => [...parameterKeys.all, "by-code", code] as const,
  orgByCode: (orgId: string, code: string) =>
    [...parameterKeys.all, "by-code", orgId, code] as const,
  search: (orgId: string, filters?: Record<string, unknown>) =>
    [...parameterKeys.all, "search", orgId, ...(filters ? [filters] : [])] as const,
};

export const permitKeys = {
  all: ["permits"] as const,
  list: (orgId: string, filters?: Record<string, unknown>) =>
    [...permitKeys.all, "list", orgId, ...(filters ? [filters] : [])] as const,
  detail: (orgId: string, permitId: string) =>
    [...permitKeys.all, "detail", orgId, permitId] as const,
  facilityList: (orgId: string, facilityId: string, filters?: Record<string, unknown>) =>
    [...permitKeys.all, "facility", orgId, facilityId, ...(filters ? [filters] : [])] as const,
};

export const permitParameterKeys = {
  all: ["permit-parameters"] as const,
  list: (orgId: string, permitId: string, filters?: Record<string, unknown>) =>
    [...permitParameterKeys.all, "list", orgId, permitId, ...(filters ? [filters] : [])] as const,
};

/**
 * Query keys for monitoring parameters (location-scoped and permit-scoped).
 * Distinct from `parameterKeys` (system/reference catalog) and
 * `permitParameterKeys` (legacy permit-only, to be removed after migration).
 * NOTE: filter objects passed to these keys MUST be stable (useMemo or useState)
 * to avoid infinite refetch loops — see .claude/rules/code.md (stable references).
 */
export const monitoringParameterKeys = {
  all: ["monitoring-parameters"] as const,
  byPermit: (orgId: string, permitId: string, filters?: Record<string, unknown>) =>
    [
      ...monitoringParameterKeys.all,
      "permit",
      orgId,
      permitId,
      ...(filters ? [filters] : []),
    ] as const,
  byLocation: (orgId: string, locationId: string, filters?: Record<string, unknown>) =>
    [
      ...monitoringParameterKeys.all,
      "location",
      orgId,
      locationId,
      ...(filters ? [filters] : []),
    ] as const,
};

export const permitLimitKeys = {
  all: ["permit-limits"] as const,
};

export const categoricalKeys = {
  all: ["categorical-standards"] as const,
  list: (filters?: Record<string, unknown>) =>
    [...categoricalKeys.all, "list", ...(filters ? [filters] : [])] as const,
  detail: (id: string) => [...categoricalKeys.all, "detail", id] as const,
};

export const csvImportKeys = {
  all: ["csv-import"] as const,
  preview: (orgId: string, permitId: string) =>
    [...csvImportKeys.all, "preview", orgId, permitId] as const,
};

export const comparisonKeys = {
  all: ["permit-comparison"] as const,
  compare: (orgId: string, permitId: string, otherPermitId: string) =>
    [...comparisonKeys.all, orgId, permitId, otherPermitId] as const,
};

export const expiringKeys = {
  all: ["expiring-permits"] as const,
  list: (orgId: string, filters?: Record<string, unknown>) =>
    [...expiringKeys.all, "list", orgId, ...(filters ? [filters] : [])] as const,
};

export const documentKeys = {
  all: ["documents"] as const,
  list: (orgId: string, filters?: Record<string, unknown>) =>
    [...documentKeys.all, "list", orgId, ...(filters ? [filters] : [])] as const,
  detail: (orgId: string, docId: string) => [...documentKeys.all, "detail", orgId, docId] as const,
  permitDocuments: (orgId: string, permitId: string) =>
    [...documentKeys.all, "permit", orgId, permitId] as const,
  facilityDocuments: (orgId: string, facilityId: string) =>
    [...documentKeys.all, "facility", orgId, facilityId] as const,
  readiness: (orgId: string, params?: Record<string, unknown>) =>
    [...documentKeys.all, "readiness", orgId, ...(params ? [params] : [])] as const,
  readinessSummary: (orgId: string) => [...documentKeys.all, "readiness-summary", orgId] as const,
  expiring: (orgId: string, days?: number) =>
    [...documentKeys.all, "expiring", orgId, ...(days ? [days] : [])] as const,
  overdue: (orgId: string) => [...documentKeys.all, "overdue", orgId] as const,
  history: (orgId: string, docId: string) =>
    [...documentKeys.all, "history", orgId, docId] as const,
  asOf: (orgId: string, versionGroupId: string, date: string) =>
    [...documentKeys.all, "as-of", orgId, versionGroupId, date] as const,
  responses: (orgId: string, docId: string) =>
    [...documentKeys.all, "responses", orgId, docId] as const,
};

export const documentCategoryKeys = {
  all: ["document-categories"] as const,
  list: (orgId: string) => [...documentCategoryKeys.all, "list", orgId] as const,
};

export const samplingEventKeys = {
  all: ["sampling-events"] as const,
  list: (orgId: string, filters?: Record<string, unknown>) =>
    [...samplingEventKeys.all, "list", orgId, ...(filters ? [filters] : [])] as const,
  detail: (orgId: string, eventId: string) =>
    [...samplingEventKeys.all, "detail", orgId, eventId] as const,
  facilityList: (orgId: string, facilityId: string, filters?: Record<string, unknown>) =>
    [
      ...samplingEventKeys.all,
      "facility",
      orgId,
      facilityId,
      ...(filters ? [filters] : []),
    ] as const,
  locationList: (orgId: string, locationId: string, filters?: Record<string, unknown>) =>
    [
      ...samplingEventKeys.all,
      "location",
      orgId,
      locationId,
      ...(filters ? [filters] : []),
    ] as const,
};

export const sampleResultKeys = {
  all: ["sample-results"] as const,
  list: (orgId: string, filters?: Record<string, unknown>) =>
    [...sampleResultKeys.all, "list", orgId, ...(filters ? [filters] : [])] as const,
  detail: (orgId: string, resultId: string) =>
    [...sampleResultKeys.all, "detail", orgId, resultId] as const,
  eventResults: (orgId: string, eventId: string, filters?: Record<string, unknown>) =>
    [...sampleResultKeys.all, "event", orgId, eventId, ...(filters ? [filters] : [])] as const,
  facilityResults: (orgId: string, facilityId: string, filters?: Record<string, unknown>) =>
    [
      ...sampleResultKeys.all,
      "facility",
      orgId,
      facilityId,
      ...(filters ? [filters] : []),
    ] as const,
  parameterResults: (orgId: string, parameterId: string, filters?: Record<string, unknown>) =>
    [
      ...sampleResultKeys.all,
      "parameter",
      orgId,
      parameterId,
      ...(filters ? [filters] : []),
    ] as const,
};

export const nodiCodeKeys = {
  all: ["nodi-codes"] as const,
  list: (filters?: Record<string, unknown>) =>
    [...nodiCodeKeys.all, "list", ...(filters ? [filters] : [])] as const,
};

export const analysisMethodKeys = {
  all: ["analysis-methods"] as const,
  list: (filters?: Record<string, unknown>) =>
    [...analysisMethodKeys.all, "list", ...(filters ? [filters] : [])] as const,
};

export const exceedanceKeys = {
  all: ["exceedances"] as const,
  list: (orgId: string, filters?: Record<string, unknown>) =>
    [...exceedanceKeys.all, "list", orgId, ...(filters ? [filters] : [])] as const,
  byResult: (orgId: string, resultId: string) =>
    [...exceedanceKeys.all, "by-result", orgId, resultId] as const,
  facilityList: (orgId: string, facilityId: string, filters?: Record<string, unknown>) =>
    [...exceedanceKeys.all, "facility", orgId, facilityId, ...(filters ? [filters] : [])] as const,
  permitList: (orgId: string, permitId: string, filters?: Record<string, unknown>) =>
    [...exceedanceKeys.all, "permit", orgId, permitId, ...(filters ? [filters] : [])] as const,
  parameterList: (orgId: string, parameterId: string, filters?: Record<string, unknown>) =>
    [
      ...exceedanceKeys.all,
      "parameter",
      orgId,
      parameterId,
      ...(filters ? [filters] : []),
    ] as const,
};

export const defaultLimitKeys = {
  all: ["default-limits"] as const,
  forParameter: (orgId: string, parameterId: string, programType: string) =>
    [...defaultLimitKeys.all, "parameter", orgId, parameterId, programType] as const,
  orgDefaults: (orgId: string, parameterId: string, programType: string) =>
    [...defaultLimitKeys.all, "org", orgId, parameterId, programType] as const,
  summary: (orgId: string) => [...defaultLimitKeys.all, "summary", orgId] as const,
};

export const complianceSummaryKeys = {
  all: ["compliance-summary"] as const,
  org: (orgId: string) => [...complianceSummaryKeys.all, "org", orgId] as const,
};

export const sampleScheduleKeys = {
  all: ["sample-schedules"] as const,
  list: (orgId: string, filters?: Record<string, unknown>) =>
    [...sampleScheduleKeys.all, "list", orgId, ...(filters ? [filters] : [])] as const,
  detail: (orgId: string, scheduleId: string) =>
    [...sampleScheduleKeys.all, "detail", orgId, scheduleId] as const,
  events: (orgId: string, scheduleId: string, filters?: Record<string, unknown>) =>
    [
      ...sampleScheduleKeys.all,
      "events",
      orgId,
      scheduleId,
      ...(filters ? [filters] : []),
    ] as const,
};

export const calendarEventKeys = {
  all: ["calendar-events"] as const,
  list: (orgId: string, filters?: Record<string, unknown>) =>
    [...calendarEventKeys.all, "list", orgId, ...(filters ? [filters] : [])] as const,
};

export const monitoringGapKeys = {
  all: ["monitoring-gaps"] as const,
  list: (orgId: string, filters?: Record<string, unknown>) =>
    [...monitoringGapKeys.all, "list", orgId, ...(filters ? [filters] : [])] as const,
  summary: (orgId: string) => [...monitoringGapKeys.all, "summary", orgId] as const,
  permitCompleteness: (orgId: string, params?: Record<string, unknown>) =>
    [...monitoringGapKeys.all, "permit-completeness", orgId, ...(params ? [params] : [])] as const,
  scheduleAdherence: (orgId: string, params?: Record<string, unknown>) =>
    [...monitoringGapKeys.all, "schedule-adherence", orgId, ...(params ? [params] : [])] as const,
  scheduleAdherenceSummary: (orgId: string) =>
    [...monitoringGapKeys.all, "schedule-adherence-summary", orgId] as const,
};

export const expectedSubmissionKeys = {
  all: ["expected-submissions"] as const,
  list: (orgId: string, filters?: Record<string, unknown>) =>
    [...expectedSubmissionKeys.all, "list", orgId, ...(filters ? [filters] : [])] as const,
  detail: (orgId: string, submissionId: string) =>
    [...expectedSubmissionKeys.all, "detail", orgId, submissionId] as const,
};

/**
 * Query keys for labs. Mirrors `analysisMethodKeys` shape (non-org-scoped
 * `list(filters?)`) — the backing endpoint GET /compliance/labs is
 * intentionally org-resolved server-side from the bearer token.
 * NOTE: filter objects passed to `list` MUST be stable (useMemo or
 * useState) to avoid infinite refetch loops — see .claude/rules/code.md (stable
 * references).
 */
export const labKeys = {
  all: ["labs"] as const,
  list: (filters?: Record<string, unknown>) =>
    [...labKeys.all, "list", ...(filters ? [filters] : [])] as const,
};

/**
 * Query keys for sample-result custody event stream. The `timeline` key
 * powers the /sample-results/{id}/custody/events GET endpoint.
 */
export const sampleResultCustodyKeys = {
  all: ["sample-result-custody"] as const,
  byResult: (orgId: string, resultId: string) =>
    [...sampleResultCustodyKeys.all, "by-result", orgId, resultId] as const,
  timeline: (orgId: string, resultId: string) =>
    [...sampleResultCustodyKeys.all, "timeline", orgId, resultId] as const,
};

/**
 * Query keys for the labs-for-sampling-event derived endpoint.
 */
export const samplingEventLabsKeys = {
  all: ["sampling-event-labs"] as const,
  byEvent: (orgId: string, eventId: string) =>
    [...samplingEventLabsKeys.all, "by-event", orgId, eventId] as const,
};

/**
 * Query keys for meters. The meter endpoints carry no `/orgs/{orgId}` segment
 * — org is resolved server-side from the bearer token — so the keys are scoped
 * by their parent resource instead: `byFacility` for
 * GET /facilities/{facilityId}/meters, `byAccount` for the
 * GET /accounts/{id}/meters reverse view, and `billedByAccount` for
 * GET /accounts/{id}/billed-installations — the devices behind the account's
 * meters, whose set and rows every meter write can change, so they live under
 * the meter tree that every meter write invalidates.
 * NOTE: params objects passed to any of them MUST be stable (useMemo or useState) to
 * avoid infinite refetch loops — see .claude/rules/code.md (stable references).
 */
export const meterKeys = {
  all: ["meters"] as const,
  byFacility: (facilityId: string, params?: Record<string, unknown>) =>
    [...meterKeys.all, "by-facility", facilityId, ...(params ? [params] : [])] as const,
  byAccount: (accountId: string, params?: Record<string, unknown>) =>
    [...meterKeys.all, "by-account", accountId, ...(params ? [params] : [])] as const,
  billedByAccount: (accountId: string, params?: Record<string, unknown>) =>
    [...meterKeys.all, "billed-by-account", accountId, ...(params ? [params] : [])] as const,
};

/**
 * Query keys for self-service reporting: the code-defined catalog
 * (datasets/templates/template-fields), the preview round trip, saved
 * reports, saved dashboards, and the caller's home pointer at one of them.
 * `orgId` rides EVERY key — the catalog and every document are per-org data.
 * `dataset` and `templateId` pass through verbatim wherever the catalog
 * supplies them (a dotted key like `tests.section` is a legal path segment and
 * must survive intact).
 */
export const reportingKeys = {
  all: ["reporting"] as const,
  datasets: (orgId: string) => [...reportingKeys.all, "datasets", orgId] as const,
  templates: (orgId: string, dataset: string) =>
    [...reportingKeys.all, "templates", orgId, dataset] as const,
  templateFields: (orgId: string, dataset: string, templateId: string) =>
    [...reportingKeys.all, "template-fields", orgId, dataset, templateId] as const,
  preview: (orgId: string, requestJson: string) =>
    [...reportingKeys.all, "preview", orgId, requestJson] as const,
  /**
   * The rows behind one clicked group. Keyed by the SOURCE and the REQUEST as
   * JSON text — the drill runs against a saved report, a dashboard card or an
   * unsaved document, and the raw text is what separates them without this
   * factory knowing any of their shapes.
   */
  reportDrill: (orgId: string, sourceJson: string, requestJson: string) =>
    [...reportingKeys.all, "drill", orgId, sourceJson, requestJson] as const,
  reports: () => [...reportingKeys.all, "reports"] as const,
  reportList: (orgId: string, params?: Record<string, unknown>) =>
    [...reportingKeys.reports(), "list", orgId, ...(params ? [params] : [])] as const,
  reportDetail: (orgId: string, reportId: string) =>
    [...reportingKeys.reports(), "detail", orgId, reportId] as const,
  reportRun: (orgId: string, reportId: string, page?: Record<string, unknown>) =>
    [...reportingKeys.reports(), "run", orgId, reportId, ...(page ? [page] : [])] as const,
  dashboards: () => [...reportingKeys.all, "dashboards"] as const,
  dashboardList: (orgId: string, params?: Record<string, unknown>) =>
    [...reportingKeys.dashboards(), "list", orgId, ...(params ? [params] : [])] as const,
  dashboardDetail: (orgId: string, dashboardId: string) =>
    [...reportingKeys.dashboards(), "detail", orgId, dashboardId] as const,
  /**
   * One dashboard's run. A run under viewer OVERRIDES is its OWN entry — the
   * same dashboard read over a different period or a different set of premises
   * is a different answer — while the unfiltered run keeps the two-argument key
   * it has always had, and that key stays a PREFIX of every filtered variant, so
   * one invalidation still reaches them all.
   */
  dashboardRun: (orgId: string, dashboardId: string, overrides?: Record<string, unknown>) =>
    [
      ...reportingKeys.dashboards(),
      "run",
      orgId,
      dashboardId,
      ...(overrides ? [overrides] : []),
    ] as const,
  /**
   * The caller's home POINTER — which of the org's shared dashboards is theirs.
   * A leaf directly under `all`, deliberately NOT under `dashboards()`: the two
   * are invalidated for different reasons, and nesting would couple them in
   * both directions — invalidating the dashboards subtree would re-read the
   * pointer, and invalidating the pointer would re-read every dashboard.
   */
  home: (orgId: string) => [...reportingKeys.all, "home", orgId] as const,
};
